import { api, API } from "./_client";

export async function listPayables() {
    const { data } = await api.get("/payables");
    return data.items || [];
}

export async function createPayable(payload) {
    const { data } = await api.post("/payables", payload);
    return data;
}

export async function updatePayable(id, payload) {
    const { data } = await api.patch(`/payables/${id}`, payload);
    return data;
}

export async function deletePayable(id) {
    const { data } = await api.delete(`/payables/${id}`);
    return data;
}

export async function listPayableSites() {
    const { data } = await api.get("/payables/sites");
    return data.sites || [];
}

// iter-217 — Returns the shared pastel palette for the per-row color
// picker (locked to the same hex list used by Excel export).
export async function listPayablesPalette() {
    const { data } = await api.get("/payables/palette");
    return data.palette || [];
}

// iter-217 — Upload an invoice file (image/* or application/pdf) to
// object storage. Returns the stored descriptor that goes on the
// payable's `invoice_file` field.
export async function uploadPayableInvoice(file) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/payables/upload-invoice", form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

// Return the URL the preview <img>/<a> can hit. Browser must supply
// the auth token itself; this is a simple non-signed URL guarded on
// the server by the user-prefix / peer-access check.
export function payableInvoiceFileUrl(storagePath) {
    return `${API}/payables/invoice-file?storage_path=${encodeURIComponent(storagePath)}`;
}

// Fetch invoice file as blob URL — frontend uses this for preview
// so we can send the auth header without exposing the token in the URL.
export async function fetchPayableInvoiceBlob(storagePath) {
    const token = localStorage.getItem("planflow.auth_token");
    const res = await fetch(
        payableInvoiceFileUrl(storagePath),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) throw new Error(`Preview failed (${res.status})`);
    const blob = await res.blob();
    return window.URL.createObjectURL(blob);
}

// Call the vision/OCR parser. Returns {fields, missing, missing_labels}.
export async function parsePayableInvoice(storagePath, mime) {
    const { data } = await api.post("/payables/parse-invoice", {
        storage_path: storagePath,
        mime: mime || "application/octet-stream",
    });
    return data;
}

// iter-221 — Attach an additional file to an existing payable row.
// Returns the updated payable (full invoice_files list).
export async function attachPayableFile(payableId, file) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post(`/payables/${payableId}/attach-file`, form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

// iter-221 — Remove a specific file from a payable's invoice_files
// list by its storage_path. Server deletes the object from storage.
export async function detachPayableFile(payableId, storagePath) {
    const { data } = await api.post(
        `/payables/${payableId}/detach-file?storage_path=${encodeURIComponent(storagePath)}`,
    );
    return data;
}

// iter-218 — One-shot: upload a file, run AI vision/OCR on it, and
// create the Payables row — all in a single server round-trip. Used
// by the Batch Import flow (frontend iterates sequentially over N
// selected files). Returns { payable, missing, missing_labels }.
export async function importPayableInvoice(file) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/payables/import-one", form, {
        headers: { "Content-Type": "multipart/form-data" },
        // AI parse can take 15-30s on a dense invoice; override axios default.
        timeout: 120_000,
    });
    return data;
}

// iter-223 — Bulk-import payables from an .xlsx workbook (expenses
// sheet with Customer / Site Address / Amount etc. columns). Each
// row's cell fill colour is preserved as the Payable's row colour.
// Returns { created, skipped, sheet }.
export async function importPayablesExcel(file) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/payables/import-excel", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
    });
    return data;
}



// Trigger an .xlsx download in the browser via a short-lived blob URL.
// We hit the API directly (not through the axios client's JSON parser)
// so the binary Workbook survives the trip intact.
export async function downloadPayablesExcel() {
    const url = `${API}/payables/export.xlsx`;
    const token = localStorage.getItem("planflow.auth_token");
    const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    const blob = await res.blob();
    const href = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    a.download = `steeltooth-payables-${stamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(href), 1000);
}
